import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../AuthContext";
import { PlatformBadge, StatusBadge, PLATFORM_META, inr } from "../platforms";
import { ArrowRight, ClipboardCheck } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-500 text-sm">Loading...</div>;

  const s = data.by_status;
  const cards = [
    { label: "Drafts", key: "draft" },
    { label: "Pending Approval", key: "pending_approval" },
    { label: "Approved", key: "approved" },
    { label: "E-Invoiced", key: "einvoiced" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Overview</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
            Welcome, {user.name.split(" ")[0]}
          </h1>
        </div>
        {(user.role === "admin" || user.role === "maker") && (
          <Link data-testid="dashboard-new-invoice-btn" to="/invoices/new"
            className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">
            New Invoice <ArrowRight size={16} />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 hover:shadow-sm transition-shadow duration-200">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className="text-3xl font-bold mt-2 text-slate-900" style={{ fontFamily: "Outfit" }}>{s[c.key]?.count || 0}</div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{inr(s[c.key]?.value || 0)}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <ClipboardCheck size={16} className="text-amber-600" />
            <h2 className="font-semibold text-slate-900">Pending Approvals</h2>
            <span className="ml-auto text-xs bg-amber-50 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5 font-semibold">{data.pending.length}</span>
          </div>
          <div data-testid="pending-approvals-list" className="divide-y divide-slate-100">
            {data.pending.length === 0 && <div className="px-5 py-8 text-sm text-slate-400">Nothing waiting for approval.</div>}
            {data.pending.map((inv) => (
              <button key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}
                data-testid={`pending-invoice-${inv.invoice_no}`}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors duration-200 text-left">
                <PlatformBadge platform={inv.platform} size={15} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold font-mono text-slate-900 truncate">{inv.invoice_no}</div>
                  <div className="text-xs text-slate-500">{inv.bill_to?.name}</div>
                </div>
                <div className="text-sm font-mono text-slate-700">{inr(inv.totals?.grand_total)}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Recent Invoices</h2>
            <Link to="/invoices" className="text-xs font-semibold text-[#0047AB] hover:underline">View all</Link>
          </div>
          <div data-testid="recent-invoices-list" className="divide-y divide-slate-100">
            {data.recent.length === 0 && <div className="px-5 py-8 text-sm text-slate-400">No invoices yet. Create your first one.</div>}
            {data.recent.map((inv) => (
              <button key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors duration-200 text-left">
                <PlatformBadge platform={inv.platform} size={15} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold font-mono text-slate-900 truncate">{inv.invoice_no}</div>
                  <div className="text-xs text-slate-500">{PLATFORM_META[inv.platform]?.name}</div>
                </div>
                <StatusBadge status={inv.status} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
